import {
	IconSlide,
	IconSlideConfig,
	ImageSlide,
	RoleRestrictedIconSlide,
	RoleRestrictedImageSlide,
	Slide,
	SlideConfig
} from 'sharebase-app/domain';
import { 
	RoleRestrictedSlideFactory, 
	UniformTemplatedSlidesController 
} from '../uniformTemplatedSlides/uniformTemplatedSlides';
import {
	AuthService,
	PhoenixToggleService
} from 'phoenix/services';
import * as mergeWith from 'lodash.mergewith'
import { CommonUtilsService } from 'util/util';
import { createSlides } from './feature-slides';
import { IStorageAccessor } from 'util/domain';
export class FeatureSlide extends Slide {
	public forceShow = false;
	constructor(
		$q:ng.IQService,
		gettextCatalog:ng.gettext.gettextCatalog,
		pxAuthService:AuthService,
		pxPhoenixToggleService:PhoenixToggleService,
		public featureName:string,
		slideData?:SlideConfig
	) {
		super($q, gettextCatalog, slideData);

		this.addValidator(() => {
			return pxPhoenixToggleService.getFeature(featureName).then(feature => {
				if(!feature.enabled){
					return false;
				}
				if(this.forceShow) {
					return true;
				}
				if (!feature.dateToggled || !pxAuthService.userPreviousLogin){
					return false;
				}
				
				return feature.dateToggled > pxAuthService.userPreviousLogin && feature.dateToggled < new Date();
			});
		})
    }
}

export class FeatureSlideFactory {
	public static $inject = [
		'$q',
		'pxAuthService',
		'pxPhoenixToggleService',
		'sbaRoleRestrictedSlideFactory',
		'gettextCatalog'
	];

	constructor(
		private $q:ng.IQService,
		private pxAuthService:AuthService,
		private pxPhoenixToggleService:PhoenixToggleService,
		private sbaRoleRestrictedSlideFactory:RoleRestrictedSlideFactory,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {
	}

	public create(featureName:string) {
		return new FeatureSlide(
			this.$q,
			this.gettextCatalog,
			this.pxAuthService,
			this.pxPhoenixToggleService,
			featureName,
		);
	}

	public createIcon(iconSlideData:IconSlideConfig, featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
            ),
            new IconSlide(
				this.$q,
				this.gettextCatalog,
				iconSlideData
			)
        ) as FeatureSlide & IconSlide;
    }

    public createImage(slideData:SlideConfig, imageFileName:string, featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
            ),
            new ImageSlide(
				this.$q,
				this.gettextCatalog,
				imageFileName,
				slideData
			)
        ) as FeatureSlide & ImageSlide;
	}
	
	public createLinkToVideo(slideData:SlideConfig, imageFileName:string, featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
			),
			new IconSlide(
				this.$q,
				this.gettextCatalog,
				{
					materialIconName:'play_arrow',
					slideData
				}),
			new ImageSlide(
				this.$q, 
				this.gettextCatalog, 
				imageFileName, 
				slideData
			),
			this.concatenateArrays
        ) as FeatureSlide & ImageSlide & IconSlide;
	}

	public createIconRestricted(iconSlideData:IconSlideConfig, allowedRoles:string[], featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
			),
			this.sbaRoleRestrictedSlideFactory.createIcon(iconSlideData, allowedRoles),
			this.concatenateArrays
        ) as FeatureSlide & RoleRestrictedIconSlide;
    }

    public createImageRestricted(slideData:SlideConfig, imageFileName:string, allowedRoles:string[], featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
            ),
			this.sbaRoleRestrictedSlideFactory.createImage(slideData, imageFileName, allowedRoles),
			this.concatenateArrays
        ) as FeatureSlide & RoleRestrictedImageSlide;
	}
	
	public createLinkToVideoRestricted(slideData:SlideConfig, imageFileName:string, allowedRoles:string[], featureName:string) {
        return mergeWith(
            new FeatureSlide(
				this.$q,
				this.gettextCatalog,
				this.pxAuthService,
				this.pxPhoenixToggleService,
				featureName
			),
			new IconSlide(
				this.$q,
				this.gettextCatalog,
				{
					materialIconName:'play_arrow',
					slideData
				}),
			this.sbaRoleRestrictedSlideFactory.createImage(slideData, imageFileName, allowedRoles),
			this.concatenateArrays
        ) as FeatureSlide & RoleRestrictedImageSlide & RoleRestrictedIconSlide;
	}
	
    private concatenateArrays(objValue, srcValue) {
        if (Array.isArray(objValue)) {
          	return objValue.concat(srcValue);
        }
    }
}

function isFeatureSlide(item:Slide): item is FeatureSlide {
    return Object.prototype.hasOwnProperty.call(item, 'featureName');
}
const REMIND_STORAGE_KEY = 'sba-remind-me-features';
export class FeatureHighlightSlidesController extends UniformTemplatedSlidesController<FeatureSlide> {
	public remindMe = this.getRemindMe();
	public static $inject = [
		'$mdPanel',
        '$mdMedia',
        '$timeout',
		'$interval',
		'$window',
		'utils',
		'localStorageService',
		'sbaFeatureSlideFactory',
		'gettextCatalog'
	];
	
	constructor(
		$mdPanel,
		$mdMedia,
		$timeout,
		$interval,
		$window,
		utils:CommonUtilsService,
		private storage:IStorageAccessor,
		sbaFeatureSlideFactory:FeatureSlideFactory,
		gettextCatalog:ng.gettext.gettextCatalog
	) {
		super($mdPanel, $mdMedia, $timeout, $interval, $window, utils);

		this.allowSkipping = true;
		this.slides = createSlides(sbaFeatureSlideFactory, gettextCatalog);
		//Allow for 'check out the new enhancements' first slide always being there
		//Need at least 1 feature slide
		this.minSlideCount = 1;
	}

	public show(force:boolean = this.remindMe){
		if(force){
			this.setForceShow(true);
		}

		return super.show().then(shown => {
			this.setForceShow(false);
			return shown;
		});
	}

	public onRemindMeChange(){
		this.storage.setStoredValue(REMIND_STORAGE_KEY, this.remindMe.toString());
	}

	private getRemindMe(){
		let storageValue = this.storage.getStoredValue(REMIND_STORAGE_KEY);
		let remindMe = true;
		if(storageValue){
			try {
				remindMe = JSON.parse(storageValue);
			}
			catch(e){}
		}

		return remindMe;
	}

	private setForceShow(force:boolean){
		this.showNavButtons = force;
		this.slides.forEach(s => s.forceShow = force);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/slides/featureHighlightSlides/featureHighlightSlides.ts